import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraFormField, SpecraInput, SpecraSelect, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { ContractVehicle } from '../../../../../../gen/ts/mono/datagateway/v1/delivery_pb';
import { VehicleProfile } from '../../../../../../gen/ts/mono/datagateway/v1/vehicle_identity_pb';
import { NaicsCodeCombobox } from '../../../components/SharedRecord/NaicsCodeCombobox';
import { GovernmentOrgPicker } from '../_shared/components/GovernmentOrgPicker';
import { PiidMatchHint } from './Contracts/PiidMatchHint';
import { dateInput, dateValue, editVehicleProfile, orderingEligibilityOptions, vehicleSaveError, vehicleTypeLabel, vehicleTypes } from './contractVehicleUi';
import { useVehicleWrites } from './vehicleIdentityHooks';
export function VehicleProfileEditor({ profile, onSaved, onCancel, onReload }) {
    const [draft, setDraft] = useState(()=>profile?.clone() ?? new VehicleProfile({
            vehicle: new ContractVehicle({
                id: crypto.randomUUID()
            })
        }));
    const [aliases, setAliases] = useState(()=>profile?.aliases.join('\n') ?? '');
    const writes = useVehicleWrites();
    const vehicle = draft.vehicle ?? new ContractVehicle();
    const set = (changes)=>setDraft(editVehicleProfile(draft, changes));
    return /*#__PURE__*/ _jsxs("form", {
        className: "space-y-3 rounded border p-4",
        "data-testid": Testids.vehicleIdentity.profileForm,
        onSubmit: async (e)=>{
            e.preventDefault();
            try {
                const saved = await writes.profile.mutateAsync(new VehicleProfile({
                    vehicle,
                    aliases: aliases.split('\n').map((a)=>a.trim()).filter(Boolean)
                }));
                if (saved.profile) onSaved(saved.profile);
            } catch  {
            /* mutation error retains draft */ }
        },
        children: [
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Vehicle name",
                children: /*#__PURE__*/ _jsx(SpecraInput, {
                    "aria-label": "Vehicle name",
                    required: true,
                    maxLength: 200,
                    value: vehicle.name,
                    onChange: (e)=>set({
                            name: e.target.value
                        })
                })
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Full name",
                children: /*#__PURE__*/ _jsx(SpecraInput, {
                    "aria-label": "Full name",
                    "data-testid": Testids.vehicleIdentity.vehicleFullName,
                    maxLength: 300,
                    value: vehicle.fullName,
                    onChange: (e)=>set({
                            fullName: e.target.value
                        })
                })
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Who can order",
                children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                    "aria-label": "Who can order",
                    "data-testid": Testids.vehicleIdentity.orderingEligibility,
                    value: vehicle.orderingEligibility,
                    onChange: (e)=>set({
                            orderingEligibility: e.target.value
                        }),
                    children: [
                        /*#__PURE__*/ _jsx("option", {
                            value: "",
                            children: "Unknown"
                        }),
                        orderingEligibilityOptions.filter((o)=>o.value !== 'UNKNOWN').map((o)=>/*#__PURE__*/ _jsx("option", {
                                value: o.value,
                                children: o.label
                            }, o.value))
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Type",
                children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                    "aria-label": "Type",
                    value: vehicle.vehicleType,
                    onChange: (e)=>set({
                            vehicleType: e.target.value
                        }),
                    children: [
                        /*#__PURE__*/ _jsx("option", {
                            value: "",
                            children: "Unknown"
                        }),
                        vehicleTypes.map((t)=>/*#__PURE__*/ _jsx("option", {
                                value: t,
                                children: vehicleTypeLabel(t)
                            }, t))
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(GovernmentOrgPicker, {
                id: "vehicle-awarding-org",
                label: "Awarding agency",
                value: vehicle.awardingAgencyOrgId,
                onChange: (id)=>set({
                        awardingAgencyOrgId: id,
                        agencyUnresolved: !id
                    }),
                testid: Testids.deliveryRegistries.vehicleAgencyOrgPicker
            }),
            /*#__PURE__*/ _jsxs("p", {
                className: "text-xs text-muted-foreground",
                children: [
                    "Source agency: ",
                    vehicle.legacySourceAgency || vehicle.agencyAcronym || 'Unknown',
                    ". Select an actual organization to resolve; initials never choose an identity."
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Mechanism identifier",
                children: /*#__PURE__*/ _jsx(SpecraInput, {
                    "aria-label": "Mechanism identifier",
                    value: vehicle.contractNumber,
                    onChange: (e)=>set({
                            contractNumber: e.target.value
                        }),
                    maxLength: 100
                })
            }),
            /*#__PURE__*/ _jsx(PiidMatchHint, {
                piid: vehicle.contractNumber
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Status",
                children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                    "aria-label": "Status",
                    required: true,
                    value: vehicle.status,
                    onChange: (e)=>set({
                            status: e.target.value
                        }),
                    children: [
                        /*#__PURE__*/ _jsx("option", {
                            value: "",
                            children: "Choose status"
                        }),
                        /*#__PURE__*/ _jsx("option", {
                            value: "ACTIVE",
                            children: "Active"
                        }),
                        /*#__PURE__*/ _jsx("option", {
                            value: "INACTIVE",
                            children: "Inactive / archived"
                        }),
                        vehicle.status && ![
                            'ACTIVE',
                            'INACTIVE'
                        ].includes(vehicle.status) && /*#__PURE__*/ _jsx("option", {
                            value: vehicle.status,
                            children: vehicle.status
                        })
                    ]
                })
            }),
            [
                'orderingPeriodStart',
                'orderingPeriodEnd'
            ].map((key, i)=>/*#__PURE__*/ _jsx(SpecraFormField, {
                    label: i ? 'Ordering end' : 'Ordering start',
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        "aria-label": i ? 'Ordering end' : 'Ordering start',
                        type: "date",
                        value: dateInput(vehicle[key]),
                        onChange: (e)=>set({
                                [key]: dateValue(e.target.value)
                            })
                    })
                }, key)),
            /*#__PURE__*/ _jsx(NaicsCodeCombobox, {
                label: "NAICS",
                value: vehicle.naicsCode,
                onChange: (naicsCode)=>set({
                        naicsCode
                    })
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Description",
                children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                    "aria-label": "Description",
                    value: vehicle.description,
                    onChange: (e)=>set({
                            description: e.target.value
                        })
                })
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Aliases (one per line, maximum 40)",
                children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                    "aria-label": "Aliases",
                    "data-testid": Testids.vehicleIdentity.aliases,
                    value: aliases,
                    onChange: (e)=>setAliases(e.target.value)
                })
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-muted-foreground",
                children: "Aliases name this same mechanism. They do not merge successor vehicles, holder contracts or task orders. Unknown ordering dates remain unknown."
            }),
            writes.profile.error && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                children: vehicleSaveError(writes.profile.error)
            }),
            writes.profile.error && onReload && /*#__PURE__*/ _jsx(SpecraButton, {
                type: "button",
                "data-testid": Testids.vehicleIdentity.reload,
                onClick: onReload,
                children: "Reload latest (discard draft)"
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex gap-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        type: "submit",
                        "data-testid": Testids.vehicleIdentity.saveProfile,
                        disabled: writes.profile.isPending,
                        children: writes.profile.isPending ? 'Saving…' : 'Save profile'
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        type: "button",
                        variant: "secondary",
                        onClick: onCancel,
                        children: "Cancel"
                    })
                ]
            })
        ]
    });
}
